import { useCallback, useEffect, useMemo, useState } from "react";
import { cleanNotifications, showNotification } from "@mantine/notifications";
import pick from "lodash/pick";
import {
    CampaignProductReviewDestinationsQuery,
    CampaignQuery,
    CampaignsQuery,
    CampaignState,
    OnCampaignUpdateSubscription,
    Questionnaire,
} from "src/graphql/generated/graphql";
import { approveCampaignMutation } from "src/graphql/operations/campaigns/mutations";
import {
    campaignProductReviewDestinationsQuery,
    campaignQuery,
    campaignQuestionnaireQuery,
    campaignsQuery,
} from "src/graphql/operations/campaigns/queries";
import { onCampaignUpdateSubscription } from "src/graphql/operations/campaigns/subscriptions";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";
import { graphqlSubscribe } from "src/graphql/utils/graphqlSubscribe";

import { handleError } from "../utils/error";
import { usePaginatedSlice, usePagination } from "./pagination";

export const campaignImageCrop = {
    minWidth: 500,
    aspectRatio: {
        value: 16 / 11,
        label: "16 / 11",
    },
};

export const campaignStateWeights: Record<CampaignState, number> = {
    [CampaignState.Draft]: 1,
    [CampaignState.Publishable]: 2,
    [CampaignState.Approved]: 3,
    [CampaignState.Published]: 4,
    [CampaignState.Opened]: 5,
    [CampaignState.Closed]: 6,
    [CampaignState.Finished]: 7,
};

export const DeliveryState = {
    DeliverByUs: "deliverByUs",
    DeliveryByClient: "deliveryByClient",
    DeliveryByEshop: "deliveryByEshop",
    PersonalPickup: "personalPickup",
} as const;

export type DeliveryState = (typeof DeliveryState)[keyof typeof DeliveryState];

export interface CampaignsOptions {
    client: string;
    state?: CampaignState;
    searchExpression?: string;
}

export type UseCampaignsReturn = ReturnType<typeof useCampaigns>;

export const useCampaigns = (options: CampaignsOptions) => {
    const [campaigns, setCampaigns] = useState<CampaignsQuery["campaigns"]["items"]>([]);
    const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);

    const pagination = usePagination({
        mode: "local",
    });

    const fetchCampaigns = useCallback(async (options: CampaignsOptions, nextToken?: string) => {
        setIsLoadingCampaigns(true);

        let hasNext = false;
        try {
            const response = await graphqlFetch(campaignsQuery, { ...options, nextToken });

            setCampaigns((campaigns) => [...(campaigns || []), ...(response.campaigns?.items || [])]);

            if (response.campaigns?.nextToken) {
                hasNext = true;
                fetchCampaigns(options, response.campaigns.nextToken);
            }
        } catch (err) {
            handleError(err, {
                message: { key: "campaigns_load_failed", namespace: "common" },
                context: {
                    clientId: options.client,
                    state: options.state,
                    searchExpression: options.searchExpression,
                    nextToken,
                },
            });
        } finally {
            if (!hasNext) {
                setIsLoadingCampaigns(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!options?.client) {
            return;
        }

        setCampaigns([]);
        fetchCampaigns(options);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(options)]);

    const filteredCampaigns = useMemo(() => {
        let filteredCampaigns = campaigns;

        if (options?.searchExpression) {
            filteredCampaigns = filteredCampaigns.filter((campaign) => {
                const campaignFields = pick(campaign, ["name", "createdAt"]);
                return Object.values(campaignFields).some((field) => {
                    return String(field).toLowerCase().includes(options.searchExpression.toLowerCase());
                });
            });
        }

        return filteredCampaigns;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(options), campaigns]);

    useEffect(() => {
        pagination.setLocalTotal(filteredCampaigns?.length || 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredCampaigns?.length]);

    return {
        campaigns: usePaginatedSlice(filteredCampaigns, pagination),
        campaignsTotal: filteredCampaigns?.length,
        campaignsPagination: pagination,
        isLoadingCampaigns,
        reloadCampaigns(reloadOptions: CampaignsOptions = options) {
            setCampaigns([]);
            fetchCampaigns(reloadOptions);
        },
    };
};

export type CampaignQueryDataType = CampaignQuery["campaign"];

export const useCampaign = (campaignId: string) => {
    const [campaign, setCampaign] = useState<CampaignQueryDataType>(null);
    const [isLoadingCampaign, setIsLoadingCampaign] = useState(!!campaignId);

    const fetchCampaign = useCallback(async (campaignId: string) => {
        setIsLoadingCampaign(true);

        try {
            const response = await graphqlFetch(campaignQuery, { campaignId });

            setCampaign(response.campaign);
        } catch (err) {
            handleError(err, {
                message: { key: "campaign_load_failed", namespace: "common" },
                context: { campaignId },
            });
        } finally {
            setIsLoadingCampaign(false);
        }
    }, []);

    useEffect(() => {
        if (!campaignId) {
            return;
        }

        fetchCampaign(campaignId);
    }, [campaignId, fetchCampaign]);

    return {
        campaign,
        isLoadingCampaign,
        reloadCampaign() {
            fetchCampaign(campaignId);
        },
    };
};

export const useCampaignProductReviewDestinations = (campaignId: string, silent: boolean = false) => {
    const [productReviewDestinations, setProductReviewDestinations] = useState<
        CampaignProductReviewDestinationsQuery["campaign"]["productReviewDestinations"]
    >([]);
    const [productReviewTranslationDestinations, setProductReviewTranslationDestinations] = useState<
        CampaignProductReviewDestinationsQuery["campaign"]["productReviewTranslationDestinations"]
    >([]);
    const [isLoadingProductReviewDestinations, setIsLoadingProductReviewDestinations] = useState(Boolean(campaignId));

    const fetchProductReviewDestinations = useCallback(
        async (campaignId: string) => {
            setIsLoadingProductReviewDestinations(true);

            try {
                const response = await graphqlFetch(campaignProductReviewDestinationsQuery, { campaignId });

                setProductReviewDestinations(response.campaign?.productReviewDestinations);
                setProductReviewTranslationDestinations(response.campaign?.productReviewTranslationDestinations);
            } catch (err) {
                handleError(
                    err,
                    silent
                        ? { silent: true, context: { campaignId } }
                        : { message: { key: "campaign_load_failed", namespace: "common" }, context: { campaignId } }
                );
            } finally {
                setIsLoadingProductReviewDestinations(false);
            }
        },
        [silent]
    );

    useEffect(() => {
        if (!campaignId) {
            return;
        }

        fetchProductReviewDestinations(campaignId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]);

    return {
        productReviewDestinations,
        productReviewTranslationDestinations,
        isLoadingProductReviewDestinations,
        reloadProductReviewDestinations() {
            fetchProductReviewDestinations(campaignId);
        },
    };
};

export const useCampaignQuestionnaire = (campaignId: string) => {
    const [questionnaire, setQuestionnaire] = useState<Questionnaire>(null);
    const [isLoadingQuestionnaire, setIsLoadingQuestionnaire] = useState(false);

    const fetchQuestionnaire = useCallback(async (campaignId: string) => {
        setIsLoadingQuestionnaire(true);

        try {
            const response = await graphqlFetch(campaignQuestionnaireQuery, { campaignId });

            setQuestionnaire(response.campaign?.questionnaire);
        } catch (err) {
            handleError(err, {
                message: { key: "campaign_load_failed", namespace: "common" },
                context: { campaignId },
            });
        } finally {
            setIsLoadingQuestionnaire(false);
        }
    }, []);

    useEffect(() => {
        if (!campaignId) {
            return;
        }

        fetchQuestionnaire(campaignId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]);

    return {
        questionnaire,
        isLoadingQuestionnaire,
        reloadQuestionnaire() {
            fetchQuestionnaire(campaignId);
        },
    };
};

export const useQueryCampaignQuestionnaire = () => {
    const queryCampaignQuestionnaire = useCallback(async (campaignId: string) => {
        try {
            const response = await graphqlFetch(campaignQuestionnaireQuery, { campaignId });

            return response.campaign?.questionnaire;
        } catch (err) {
            handleError(err, {
                message: { key: "campaign_load_failed", namespace: "common" },
                context: { campaignId },
            });
        }
    }, []);

    return { queryCampaignQuestionnaire };
};

export const useApproveCampaign = (campaignId: string) => {
    const [isApprovingCampaign, setIsApprovingCampaign] = useState(false);

    const { subscribeToCampaignUpdate } = useSubscribeToCampaignUpdate(campaignId);

    const approveCampaign = useCallback(async () => {
        setIsApprovingCampaign(true);
        showNotification({
            color: "blue",
            message: "campaign_approving",
            loading: true,
            autoClose: false,
        });

        try {
            const race = Promise.race([
                new Promise<void>(async (resolve) => {
                    const unsubscribe = await subscribeToCampaignUpdate(({ reason }) => {
                        if (reason === `scheduled/stateChange/${CampaignState.Approved}`) {
                            unsubscribe();
                            resolve();
                        }
                    });
                }),
                new Promise((_, reject) => {
                    setTimeout(() => {
                        reject(new Error("Timeout"));
                    }, 1000 * 15);
                }),
            ]);

            await graphqlFetch(approveCampaignMutation, { campaignId });

            await race;

            cleanNotifications();
            showNotification({
                color: "green",
                title: "campaign_approving_success",
                message: "",
            });
        } catch (err) {
            handleError(err, {
                message: { key: "campaign_approving_failed", namespace: "common" },
                context: { campaignId },
            });
        } finally {
            setIsApprovingCampaign(false);
        }
    }, [campaignId, subscribeToCampaignUpdate]);

    return {
        isApprovingCampaign,
        approveCampaign,
    };
};

export const useSubscribeToCampaignUpdate = (campaignId: string) => {
    const subscribeToCampaignUpdate = useCallback(
        (onUpdate: (value: OnCampaignUpdateSubscription["onCampaignUpdate"]) => unknown) => {
            let subscription: import("zen-observable-ts").ZenObservable.Subscription;

            try {
                subscription = graphqlSubscribe(onCampaignUpdateSubscription, { campaignId }).subscribe({
                    next({ value }) {
                        onUpdate(value?.data?.onCampaignUpdate);
                    },
                    error(err) {
                        handleError(err, { context: { campaignId } });
                    },
                });
            } catch (err) {
                handleError(err, { context: { campaignId } });
            }

            return () => {
                subscription?.unsubscribe?.();
                subscription = null;
            };
        },
        [campaignId]
    );

    return { subscribeToCampaignUpdate };
};
